/**
 * Cliente da API de conta (`/api/v2`) — Fase 4.
 *
 * Mesmas três regras de `acesso/api.ts` (Fase 2/3), agora contra o contrato
 * v2.1: cookie de sessão nunca lido nem escrito por aqui (`HttpOnly`,
 * `credentials: 'same-origin'`); nenhum token em `localStorage` ou URL; o
 * CSRF viaja pelo cookie legível próprio da conta (`nev_account_csrf`).
 */
import type { EfeitosProspectivos, ResultadoPublico } from '@meu-mapa-nev/engine';

const BASE = '/api/v2';
const CSRF_COOKIE_NAME = 'nev_account_csrf';

function readCsrfCookie(): string | undefined {
  const match = document.cookie.split('; ').find((row) => row.startsWith(`${CSRF_COOKIE_NAME}=`));
  return match?.slice(CSRF_COOKIE_NAME.length + 1);
}

export interface ApiProblem {
  readonly status: number;
  readonly code: string;
  readonly title: string;
  readonly correlationId?: string;
}

export type ApiResult<T> =
  { readonly ok: true; readonly value: T } | { readonly ok: false; readonly problem: ApiProblem };

async function request<T>(
  path: string,
  init: RequestInit & {
    withCsrf?: boolean;
    idempotencyKey?: string;
    reauthToken?: string;
    ifMatch?: number;
  } = {},
  base = BASE,
): Promise<ApiResult<T>> {
  const headers = new Headers(init.headers);
  headers.set('Accept', 'application/json');

  if (init.body !== undefined) {
    headers.set('Content-Type', 'application/json');
  }
  if (init.withCsrf === true) {
    const csrfToken = readCsrfCookie();
    if (csrfToken !== undefined) {
      headers.set('X-CSRF-Token', csrfToken);
    }
  }
  if (init.idempotencyKey !== undefined) {
    headers.set('Idempotency-Key', init.idempotencyKey);
  }
  if (init.reauthToken !== undefined) {
    headers.set('X-Reauthentication-Token', init.reauthToken);
  }
  if (init.ifMatch !== undefined) {
    headers.set('If-Match', `"${String(init.ifMatch)}"`);
  }

  let response: Response;
  try {
    response = await fetch(`${base}${path}`, { ...init, headers, credentials: 'same-origin' });
  } catch {
    return {
      ok: false,
      problem: { status: 0, code: 'NETWORK_ERROR', title: 'Não foi possível conectar ao serviço.' },
    };
  }

  if (response.status === 204) {
    return { ok: true, value: undefined as T };
  }

  // O contrato v2.1 restringe `Problem` a `{code, title, correlation_id}`,
  // sem `fields` (achado da auditoria de 11/09/2026, registrado como
  // contradição com a exigência de acessibilidade de erro-por-campo —
  // `docs/decisions/2026-09-11-P0P1-CONFORMIDADE-DE-CONTRATO.md`). Por isso
  // esta camada, diferente da de `/api/v1`, não tem `fields` para repassar.
  const body: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    const problem = (body ?? {}) as { code?: string; title?: string; correlation_id?: string };
    return {
      ok: false,
      problem: {
        status: response.status,
        code: problem.code ?? 'UNKNOWN',
        title: problem.title ?? 'Não foi possível concluir a operação.',
        ...(problem.correlation_id === undefined ? {} : { correlationId: problem.correlation_id }),
      },
    };
  }

  return { ok: true, value: body as T };
}

export type AccountState = 'consent_pending' | 'active' | 'recovery_only';
export type AccountNextStep =
  | 'accept-documents'
  | 'choose-participation'
  | 'my-journey'
  | 'no-participation'
  | 'reset-password';

export interface AccountContext {
  readonly authenticated: true;
  readonly account_state: AccountState;
  readonly next_step: AccountNextStep;
  readonly selected_participation_id: string | null;
  readonly session_expires_at: string;
}

export interface ParticipationChoice {
  readonly participation_id: string;
  readonly cohort_id: string;
  readonly cohort_name: string;
  readonly cohort_start: string;
  readonly access_state:
    'available' | 'suspended' | 'ended' | 'removed' | 'consent_required' | 'unavailable';
}

export interface AccessMethod {
  readonly id: string;
  readonly provider: string;
  readonly can_remove: boolean;
}

export interface PendingLegalDocument {
  readonly id: string;
  readonly document_code: string;
  readonly version: string;
  readonly content_hash: string;
  readonly content: string;
  readonly required_for_service: boolean;
}

export interface LegalReacceptancePending {
  readonly participation_id: string;
  readonly legal_bundle_version: string;
  readonly legal_acceptance_state: 'required';
  readonly documents: readonly PendingLegalDocument[];
}

export interface LegalReacceptanceDecision {
  readonly document_version_id: string;
  readonly document_code: string;
  readonly version: string;
  readonly content_hash: string;
  readonly decision: 'accepted' | 'refused';
}

export interface LegalReacceptanceResult {
  readonly participation_id: string;
  readonly legal_acceptance_state: 'current' | 'refused';
  readonly accepted_document_count: number;
}

/**
 * Espelha `JourneyAvailable` | `JourneyBlocked` do contrato oficial 2.1
 * (`reference/official/scope/contracts/account-journey.openapi.json`) e de
 * `JourneyResponse` em `packages/api/src/routing/journey-projection.ts`. A
 * resposta do backend não tem envelope — é diretamente uma dessas duas formas
 * (achado da quarta revisão independente de 12/09/2026, corrigido nesta
 * rodada: antes o cliente esperava `{ journey: AccountJourneyProjection }`,
 * um DTO camelCase que nunca existiu no backend real).
 */
export interface JourneySegment {
  readonly id: string;
  readonly content_key: string;
  readonly starts_on: string;
  readonly ends_on: string;
  readonly calendar_state: 'past' | 'current' | 'future';
}

export type JourneyAvailableNextStep =
  | 'initial-assessment'
  | 'awaiting-final'
  | 'final-assessment'
  | 'final-result'
  | 'documents-processing';

export interface JourneyAvailable {
  readonly status: 'available';
  readonly participation_id: string;
  readonly cohort_id: string;
  readonly cohort_name: string;
  readonly ui_content_version: string;
  readonly access_expires_at: string | null;
  readonly route_reference_date: string | null;
  readonly segments: readonly JourneySegment[];
  readonly next_step: JourneyAvailableNextStep;
  readonly final_available_on: string | null;
  readonly initial_result_id: string | null;
  readonly final_result_id: string | null;
  readonly document_ids: readonly string[];
}

export type JourneyBlockedReason =
  | 'not_eligible'
  | 'suspended'
  | 'access_ended'
  | 'data_removed'
  | 'reacceptance_required'
  | 'version_unavailable'
  | 'state_unclassified';

export interface JourneyBlocked {
  readonly status: 'blocked';
  readonly participation_id: string;
  readonly reason: JourneyBlockedReason;
  readonly message_key: string;
  readonly support_code: string | null;
}

export type Journey = JourneyAvailable | JourneyBlocked;

export interface ParticipantDocument {
  readonly id: string;
  readonly kind: 'participant-initial' | 'participant-final';
  readonly state:
    | 'not_requested'
    | 'queued'
    | 'processing'
    | 'available'
    | 'retry_wait'
    | 'failed'
    | 'superseded'
    | 'invalidated'
    | 'deleted';
  readonly created_at: string;
  readonly logical_hash?: string;
  readonly template_version?: string;
  readonly format?: DocumentFormat;
}

export type DocumentFormat = 'mobile' | 'a4';
export interface DocumentRequestReceipt {
  readonly state: 'available' | 'queued' | 'processing' | 'retry_wait';
  readonly document_id: string | null;
  readonly template_version: string;
  readonly format: DocumentFormat;
}

export interface DownloadedDocument {
  readonly blob: Blob;
  readonly filename: string;
}

async function documentProblem(response: Response): Promise<ApiProblem> {
  const body = (await response.json().catch(() => null)) as {
    code?: string;
    title?: string;
    correlation_id?: string;
  } | null;
  return {
    status: response.status,
    code: body?.code ?? 'UNKNOWN',
    title: body?.title ?? 'Não foi possível concluir a operação.',
    ...(body?.correlation_id === undefined ? {} : { correlationId: body.correlation_id }),
  };
}

function downloadFilename(response: Response): string {
  const disposition = response.headers.get('Content-Disposition') ?? '';
  const match = /filename="([^"]+)"/i.exec(disposition);
  return match?.[1] ?? 'meu-mapa-nev.pdf';
}

export interface InitialDraftState {
  readonly revision: number;
  readonly state: 'not_started' | 'draft' | 'expired';
  readonly expires_at: string | null;
  readonly ui_content_version: string;
  readonly draft?: InitialDraftData;
  readonly correction_draft?: InitialCorrectionDraft;
}

export interface InitialDraftData {
  readonly profile: {
    readonly full_name: string;
    readonly birth_date: string;
    readonly whatsapp_e164: string;
    readonly gender_option_code: string | null;
    readonly gender_self_description: string | null;
    readonly height_cm: number | null;
  };
  readonly measurements: Readonly<Partial<Record<'weight' | 'waist' | 'abdomen' | 'hip', string>>>;
  readonly triage_answers: Readonly<Record<string, string>>;
  readonly pillar_answers: Readonly<Record<string, string>>;
}

export type InitialCorrectionDraft = InitialDraftData;

export interface InitialCorrectionReceipt {
  readonly correction_status: 'submitted';
}

export type FinalMeasureKey = 'weight_kg' | 'waist_cm' | 'abdomen_cm' | 'hip_cm';
export type FinalMeasurements = Partial<Record<FinalMeasureKey, string | null>>;

export interface FinalDraftState {
  readonly revision: number;
  readonly state: 'not_started' | 'draft' | 'submitted' | 'reopened';
  readonly expires_at: string;
  readonly ui_content_version: string;
  readonly methodology_version: string;
  readonly release_binding_id: string;
  readonly route_type: 'group' | 'late';
  readonly suggested_final_on: string;
  readonly collective_release_on: string;
  readonly access_deadline: string;
  readonly initial_measurements: FinalMeasurements;
  readonly answers: Readonly<Record<string, number>>;
  readonly program_evaluation: readonly {
    readonly question_code: string;
    readonly answer: string | number | boolean;
  }[];
  readonly final_measurements: FinalMeasurements;
  readonly feedback: { readonly text?: string };
  /** Apenas as opções da escala congelada; regras internas ficam no servidor. */
  readonly answer_options: readonly { readonly code: string; readonly value: number }[];
}

export interface FinalDraftPatch {
  readonly answers?: {
    readonly answers: readonly { readonly question_code: string; readonly option_code: string }[];
  };
  readonly program_evaluation?: readonly {
    readonly question_code: string;
    readonly answer: string | number | boolean;
  }[];
  readonly final_measurements?: FinalMeasurements & {
    readonly confirmed_extreme_fields?: readonly FinalMeasureKey[];
  };
  readonly feedback?: { readonly text: string };
}

export interface FinalTestimonialDecision {
  readonly authorization_decision: 'authorized' | 'not_authorized';
  readonly testimonial_mode?: 'anonymous' | 'first_name';
  readonly text?: string;
  readonly authorization_version?: string;
  readonly authorization_content_hash?: string;
}

export interface FinalResult {
  readonly result_id: string;
  readonly kind: 'final';
  readonly initial_recalculated?: boolean;
  readonly initial_recalculated_at?: string;
  readonly methodology_version: string;
  readonly release_binding_id: string;
  readonly generated_at: string;
  readonly pillar_comparisons: readonly {
    readonly code: string;
    readonly position: number;
    readonly classification: 'positive' | 'stable' | 'negative';
    readonly public_message_key: string;
    readonly public_message: string;
  }[];
  readonly global_evolution: {
    readonly exact_delta: { readonly numerator: number; readonly denominator: number };
    readonly display_value: number;
    readonly display_text: string;
    readonly classification: 'positive' | 'stable' | 'negative';
    readonly public_message_key: string;
    readonly public_message: string;
  };
  readonly greatest_advances: readonly string[];
  readonly current_priorities: readonly string[];
  readonly body_comparisons: readonly {
    readonly kind: 'weight' | 'waist' | 'abdomen' | 'hip';
    readonly unit: 'kg' | 'cm';
    readonly initial_value: string;
    readonly final_value: string;
    readonly signed_difference: string;
    readonly signed_percentage: string;
    readonly classification: 'reduction' | 'stable' | 'increase';
    readonly public_message_key: string;
    readonly public_message: string;
  }[];
  readonly chart: {
    readonly render_path: string;
    readonly expires_at: string;
    readonly accessible_summary: string;
    readonly numeric_labels_present: false;
  };
  readonly educational_notice_key: string;
  readonly educational_notice: string;
  readonly documents: readonly {
    readonly id: string;
    readonly kind: 'participant-initial' | 'participant-final';
    readonly state: string;
  }[];
}

export interface InitialPrecheck {
  readonly complete: true;
  readonly preference_required: boolean;
  readonly candidates: readonly string[];
  readonly precheck_token: string | null;
}

export type AccountInitialResult = ResultadoPublico & {
  readonly uiContentVersion: string;
  readonly methodologyVersion: string;
  readonly releaseBindingId: string;
  readonly generatedAt: string;
  readonly supportCode?: string;
  readonly correctionEffects?: EfeitosProspectivos | null;
};

function isJourney(value: unknown): value is Journey {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as { status?: unknown; participation_id?: unknown };
  if (typeof candidate.participation_id !== 'string') return false;
  return candidate.status === 'available' || candidate.status === 'blocked';
}

export interface CohortPreview {
  readonly cohort_id: string;
  readonly name: string;
  readonly start_date: string;
  readonly end_date: string;
  readonly accepting_new: boolean;
}

export interface Enrollment {
  readonly participation_id: string;
  readonly cohort_id: string;
  readonly ui_content_version: string;
  readonly next_step:
    | 'initial-assessment'
    | 'my-journey'
    | 'not-eligible'
    | 'access-ended'
    | 'participation-suspended'
    | 'reaccept-documents'
    | 'state-unclassified';
}

export interface ApplicableDocument {
  readonly code: string;
  readonly version: string;
  readonly content_hash: string;
  readonly required_for_service: boolean;
  readonly initially_checked: boolean;
  readonly content: string;
}

export interface ReminderPreferences {
  readonly participation_id: string;
  readonly revision: number;
  readonly policy_version: string;
  readonly channels: readonly {
    readonly channel: 'email' | 'whatsapp';
    readonly category: 'journey-reminders' | 'document-notices';
    readonly enabled: boolean;
  }[];
}

export const contaApi = {
  legalReacceptance(participationId: string): Promise<ApiResult<LegalReacceptancePending>> {
    return request(`/participations/${encodeURIComponent(participationId)}/legal-reacceptance`);
  },

  recordLegalReacceptance(input: {
    participationId: string;
    decisions: readonly LegalReacceptanceDecision[];
    idempotencyKey: string;
    reauthToken: string;
  }): Promise<ApiResult<LegalReacceptanceResult>> {
    return request(
      `/participations/${encodeURIComponent(input.participationId)}/legal-reacceptance`,
      {
        method: 'POST',
        withCsrf: true,
        idempotencyKey: input.idempotencyKey,
        reauthToken: input.reauthToken,
        body: JSON.stringify({ decisions: input.decisions }),
      },
    );
  },
  reminderPreferences(participationId: string): Promise<ApiResult<ReminderPreferences>> {
    return request(`/participations/${encodeURIComponent(participationId)}/reminder-preferences`);
  },
  saveReminderPreferences(
    value: ReminderPreferences,
    key: string,
  ): Promise<ApiResult<ReminderPreferences>> {
    return request(
      `/participations/${encodeURIComponent(value.participation_id)}/reminder-preferences`,
      {
        method: 'PATCH',
        withCsrf: true,
        ifMatch: value.revision,
        idempotencyKey: key,
        body: JSON.stringify({ policy_version: value.policy_version, channels: value.channels }),
      },
    );
  },
  signup(input: {
    email: string;
    password: string;
    turnstileToken: string;
    cohortToken?: string;
  }): Promise<ApiResult<{ accepted: boolean }>> {
    return request('/account/signup', {
      method: 'POST',
      body: JSON.stringify({
        email: input.email,
        password: input.password,
        turnstile_token: input.turnstileToken,
        ...(input.cohortToken !== undefined ? { cohort_token: input.cohortToken } : {}),
      }),
    });
  },

  login(input: {
    email: string;
    password: string;
    turnstileToken: string;
  }): Promise<ApiResult<AccountContext>> {
    return request('/account/login', {
      method: 'POST',
      body: JSON.stringify({
        email: input.email,
        password: input.password,
        turnstile_token: input.turnstileToken,
      }),
    });
  },

  logout(): Promise<ApiResult<void>> {
    return request('/account/logout', { method: 'POST', withCsrf: true });
  },

  requestRecovery(input: {
    email: string;
    turnstileToken: string;
  }): Promise<ApiResult<{ accepted: boolean }>> {
    return request('/account/recovery', {
      method: 'POST',
      body: JSON.stringify({ email: input.email, turnstile_token: input.turnstileToken }),
    });
  },

  completeRecovery(input: { password: string }): Promise<ApiResult<void>> {
    return request('/account/recovery/complete', {
      method: 'POST',
      withCsrf: true,
      body: JSON.stringify({ new_password: input.password }),
    });
  },

  changePassword(input: { newPassword: string; reauthToken: string }): Promise<ApiResult<void>> {
    return request('/account/password', {
      method: 'PUT',
      withCsrf: true,
      reauthToken: input.reauthToken,
      body: JSON.stringify({ new_password: input.newPassword }),
    });
  },

  reauthenticateWithPassword(input: {
    password: string;
    purpose:
      | 'link-identity'
      | 'unlink-identity'
      | 'change-password'
      | 'revoke-sessions'
      | 'legal-reaccept';
    turnstileToken: string;
  }): Promise<ApiResult<{ reauthentication_token: string; expires_at: string }>> {
    return request('/account/reauthentication/password', {
      method: 'POST',
      withCsrf: true,
      body: JSON.stringify({
        current_password: input.password,
        purpose: input.purpose,
        turnstile_token: input.turnstileToken,
      }),
    });
  },

  reauthenticateWithGoogle(input: {
    purpose:
      | 'link-identity'
      | 'unlink-identity'
      | 'change-password'
      | 'revoke-sessions'
      | 'legal-reaccept';
  }): Promise<ApiResult<{ authorization_url: string; expires_at: string }>> {
    return request('/account/reauthentication', {
      method: 'POST',
      withCsrf: true,
      body: JSON.stringify({ purpose: input.purpose }),
    });
  },

  socialReauthenticationResult(): Promise<
    ApiResult<{ reauthentication_token: string; expires_at: string }>
  > {
    return request('/account/reauthentication/result', {
      method: 'POST',
      withCsrf: true,
    });
  },

  socialStart(input: {
    cohortToken?: string;
    turnstileToken: string;
  }): Promise<ApiResult<{ authorization_url: string; expires_at: string }>> {
    return request('/account/social/start', {
      method: 'POST',
      body: JSON.stringify({
        provider: 'google',
        ...(input.cohortToken !== undefined ? { cohort_token: input.cohortToken } : {}),
        turnstile_token: input.turnstileToken,
      }),
    });
  },

  methodsLinkStart(input: {
    reauthToken: string;
    idempotencyKey: string;
  }): Promise<ApiResult<{ authorization_url: string; expires_at: string }>> {
    return request('/account/methods/link', {
      method: 'POST',
      withCsrf: true,
      reauthToken: input.reauthToken,
      idempotencyKey: input.idempotencyKey,
      body: JSON.stringify({ provider: 'google' }),
    });
  },

  listMethods(): Promise<ApiResult<{ methods: readonly AccessMethod[] }>> {
    return request('/account/methods', { method: 'GET' });
  },

  unlinkMethod(input: {
    identityId: string;
    reauthToken: string;
    idempotencyKey: string;
  }): Promise<ApiResult<void>> {
    return request(`/account/methods/${encodeURIComponent(input.identityId)}`, {
      method: 'DELETE',
      withCsrf: true,
      reauthToken: input.reauthToken,
      idempotencyKey: input.idempotencyKey,
    });
  },

  revokeAllSessions(input: {
    reauthToken: string;
    idempotencyKey: string;
  }): Promise<ApiResult<void>> {
    return request('/account/sessions', {
      method: 'DELETE',
      withCsrf: true,
      reauthToken: input.reauthToken,
      idempotencyKey: input.idempotencyKey,
    });
  },

  context(): Promise<ApiResult<AccountContext>> {
    return request('/account/context', { method: 'GET' });
  },

  participations(): Promise<ApiResult<{ participations: readonly ParticipationChoice[] }>> {
    return request('/account/participations', { method: 'GET' });
  },

  selectParticipation(participationId: string): Promise<ApiResult<AccountContext>> {
    return request('/account/selection', {
      method: 'PUT',
      withCsrf: true,
      body: JSON.stringify({ participation_id: participationId }),
    });
  },

  async journey(participationId: string): Promise<ApiResult<Journey>> {
    const result = await request<unknown>(
      `/participations/${encodeURIComponent(participationId)}/journey`,
      {
        method: 'GET',
      },
    );
    if (!result.ok) return result;
    if (!isJourney(result.value)) {
      return {
        ok: false,
        problem: {
          status: 502,
          code: 'INVALID_API_RESPONSE',
          title: 'O serviço devolveu uma resposta inesperada.',
        },
      };
    }
    return { ok: true, value: result.value };
  },

  async documents(
    participationId: string,
  ): Promise<ApiResult<{ documents: readonly ParticipantDocument[] }>> {
    let response: Response;
    try {
      response = await fetch(
        `/api/v1/participations/${encodeURIComponent(participationId)}/documents`,
        { method: 'GET', headers: { Accept: 'application/json' }, credentials: 'same-origin' },
      );
    } catch {
      return {
        ok: false,
        problem: {
          status: 0,
          code: 'NETWORK_ERROR',
          title: 'Não foi possível conectar ao serviço.',
        },
      };
    }
    if (!response.ok) return { ok: false, problem: await documentProblem(response) };
    return {
      ok: true,
      value: (await response.json()) as { documents: readonly ParticipantDocument[] },
    };
  },

  requestDocument(
    participationId: string,
    kind: 'initial' | 'final',
    format: DocumentFormat,
    idempotencyKey: string,
  ): Promise<ApiResult<DocumentRequestReceipt>> {
    return request(
      `/participations/${encodeURIComponent(participationId)}/documents/request`,
      {
        method: 'POST',
        withCsrf: true,
        idempotencyKey,
        body: JSON.stringify({ kind, format }),
      },
      '/api/v1',
    );
  },

  async downloadDocument(
    documentId: string,
    idempotencyKey: string,
  ): Promise<ApiResult<DownloadedDocument>> {
    const headers = new Headers({ Accept: 'application/json', 'Idempotency-Key': idempotencyKey });
    const csrfToken = readCsrfCookie();
    if (csrfToken !== undefined) headers.set('X-CSRF-Token', csrfToken);
    let issued: Response;
    try {
      issued = await fetch(`/api/v1/documents/${encodeURIComponent(documentId)}/download`, {
        method: 'POST',
        headers,
        credentials: 'same-origin',
      });
    } catch {
      return {
        ok: false,
        problem: {
          status: 0,
          code: 'NETWORK_ERROR',
          title: 'Não foi possível conectar ao serviço.',
        },
      };
    }
    if (!issued.ok) return { ok: false, problem: await documentProblem(issued) };
    const ticket = (await issued.json()) as { download_path?: unknown };
    if (typeof ticket.download_path !== 'string' || !ticket.download_path.startsWith('/api/v1/')) {
      return {
        ok: false,
        problem: {
          status: 502,
          code: 'INVALID_API_RESPONSE',
          title: 'O serviço devolveu uma resposta inesperada.',
        },
      };
    }
    let downloaded: Response;
    try {
      downloaded = await fetch(ticket.download_path, {
        method: 'GET',
        headers: { Accept: 'application/pdf' },
        credentials: 'same-origin',
      });
    } catch {
      return {
        ok: false,
        problem: {
          status: 0,
          code: 'NETWORK_ERROR',
          title: 'Não foi possível conectar ao serviço.',
        },
      };
    }
    if (!downloaded.ok) return { ok: false, problem: await documentProblem(downloaded) };
    return {
      ok: true,
      value: { blob: await downloaded.blob(), filename: downloadFilename(downloaded) },
    };
  },

  cohortPreview(cohortToken: string): Promise<ApiResult<CohortPreview>> {
    return request(`/enrollment/cohort?cohort_token=${encodeURIComponent(cohortToken)}`, {
      method: 'GET',
    });
  },

  cohortDocuments(
    cohortToken: string,
  ): Promise<ApiResult<{ documents: readonly ApplicableDocument[] }>> {
    return request(`/enrollment/documents?cohort_token=${encodeURIComponent(cohortToken)}`, {
      method: 'GET',
    });
  },

  acceptEnrollment(input: {
    cohortToken: string;
    decisions: ReadonlyArray<{
      document_code: string;
      version: string;
      content_hash: string;
      decision: 'accepted' | 'refused';
    }>;
    idempotencyKey: string;
  }): Promise<ApiResult<Enrollment>> {
    return request('/enrollment/accept', {
      method: 'POST',
      withCsrf: true,
      idempotencyKey: input.idempotencyKey,
      body: JSON.stringify({ cohort_token: input.cohortToken, decisions: input.decisions }),
    });
  },

  initialDraft(participationId: string): Promise<ApiResult<InitialDraftState>> {
    return request(`/participations/${encodeURIComponent(participationId)}/initial-draft`, {
      method: 'GET',
    });
  },

  saveInitialProfile(
    participationId: string,
    revision: number,
    body: unknown,
  ): Promise<
    ApiResult<{ revision: number; state: 'draft'; saved_at: string; expires_at: string }>
  > {
    return request(`/participations/${encodeURIComponent(participationId)}/profile-draft`, {
      method: 'PATCH',
      withCsrf: true,
      ifMatch: revision,
      body: JSON.stringify(body),
    });
  },

  saveInitialAnswers(
    participationId: string,
    section: 'triage' | 'pillar',
    revision: number,
    answers: Readonly<Record<string, string>>,
  ): Promise<
    ApiResult<{ revision: number; state: 'draft'; saved_at: string; expires_at: string }>
  > {
    return request(`/participations/${encodeURIComponent(participationId)}/${section}-draft`, {
      method: 'PATCH',
      withCsrf: true,
      ifMatch: revision,
      body: JSON.stringify({
        answers: Object.entries(answers).map(([question_code, option_code]) => ({
          question_code,
          option_code,
        })),
      }),
    });
  },

  checkInitialEligibility(
    participationId: string,
    revision: number,
  ): Promise<ApiResult<{ outcome: 'eligible' | 'ineligible' }>> {
    return request(`/participations/${encodeURIComponent(participationId)}/eligibility-check`, {
      method: 'POST',
      withCsrf: true,
      ifMatch: revision,
    });
  },

  initialPrecheck(participationId: string, revision: number): Promise<ApiResult<InitialPrecheck>> {
    return request(`/participations/${encodeURIComponent(participationId)}/initial-precheck`, {
      method: 'POST',
      withCsrf: true,
      ifMatch: revision,
    });
  },

  setInitialTiePreference(
    participationId: string,
    revision: number,
    precheckToken: string,
    preferredPillarCode: string,
  ): Promise<
    ApiResult<{ revision: number; state: 'draft'; saved_at: string; expires_at: string }>
  > {
    return request(`/participations/${encodeURIComponent(participationId)}/tie-preference`, {
      method: 'PUT',
      withCsrf: true,
      ifMatch: revision,
      body: JSON.stringify({
        precheck_token: precheckToken,
        preferred_pillar_code: preferredPillarCode,
      }),
    });
  },

  submitInitial(
    participationId: string,
    revision: number,
    idempotencyKey: string,
  ): Promise<ApiResult<AccountInitialResult | InitialCorrectionReceipt>> {
    return request(`/participations/${encodeURIComponent(participationId)}/initial-submit`, {
      method: 'POST',
      withCsrf: true,
      ifMatch: revision,
      idempotencyKey,
    });
  },

  initialResult(participationId: string): Promise<ApiResult<AccountInitialResult>> {
    return request(`/participations/${encodeURIComponent(participationId)}/result-initial`, {
      method: 'GET',
    });
  },

  finalDraft(participationId: string): Promise<ApiResult<FinalDraftState>> {
    return request(`/participations/${encodeURIComponent(participationId)}/final-draft`, {
      method: 'GET',
    });
  },

  saveFinalDraft(
    participationId: string,
    revision: number,
    patch: FinalDraftPatch,
  ): Promise<
    ApiResult<{
      revision: number;
      state: 'draft';
      saved_at: string;
      expires_at: string;
    }>
  > {
    return request(`/participations/${encodeURIComponent(participationId)}/final-draft`, {
      method: 'PATCH',
      withCsrf: true,
      ifMatch: revision,
      body: JSON.stringify(patch),
    });
  },

  submitFinal(
    participationId: string,
    revision: number,
    idempotencyKey: string,
    testimonial?: FinalTestimonialDecision,
  ): Promise<ApiResult<FinalResult>> {
    return request(`/participations/${encodeURIComponent(participationId)}/final-submit`, {
      method: 'POST',
      withCsrf: true,
      ifMatch: revision,
      idempotencyKey,
      ...(testimonial === undefined ? {} : { body: JSON.stringify({ testimonial }) }),
    });
  },

  finalResult(participationId: string): Promise<ApiResult<FinalResult>> {
    return request(`/participations/${encodeURIComponent(participationId)}/result-final`, {
      method: 'GET',
    });
  },
};
